import { formatDate } from "node_modules/@angular/common";
import { APP_LOCALE, ERROR_MESSAGES } from "src/app/constants";
import { adaptAddress, Address, createEmptyAddress, isNullAddress } from "./address.model";
import { LicenseFields } from "./license.model";

export interface BusinessBranch {
  Id?: number;
  Name: string;
  Address: Address;
  ContactNo: string;
  Email?: string;
  BusinessId: number;
  Remark?: string;
  CreatedOn?: Date;
  CreatedBy?: string;
}

export function createEmptyBranch(): BusinessBranch {
  return {
    Name: '',
    Address: createEmptyAddress(),
    ContactNo: '',
    BusinessId: -1
  }
}

export type BranchWithLicense = BusinessBranch & LicenseFields;

// --- Mapper -------------------------------------------------------------------------------------

export interface BusinessBranchDTO {
  id?: number;
  name: string;
  addressLine0: string;
  addressLine1?: string;
  addressLine2?: string;
  postalZone: string;
  cityName: string;
  state: string;
  email?: string;
  contactNo: string;
  businessId: number;
  createdBy?: string;
  createdOn?: string;
}

export interface BranchWithLicenseDTO {
  businessId: number;
  name: string;
  addressLine0: string;
  addressLine1?: string;
  addressLine2?: string;
  postalZone: string;
  cityName: string;
  state: string;
  email?: string;
  contactNo: string;
  validSince: string;
  validUntil: string;
  accountNo?: string;
  remark?: string;
}

export function adaptBusinessBranch(item: Partial<BusinessBranchDTO>): BusinessBranch {
  return {
    Id: item.id ?? 0,
    Name: item.name.replace('-', ''),
    Address: adaptAddress(item),
    Email: item.email,
    ContactNo: item.contactNo,
    BusinessId: item.businessId,
    CreatedBy: item.createdBy,
    CreatedOn: (item.createdOn && new Date(item.createdOn)),
  }
}

export function toApiBusinessBranch(branch: BusinessBranch): BusinessBranchDTO {
  return {
    businessId: branch.BusinessId,
    name: branch.Name?.length > 0 ? branch.Name : '-',
    ...(branch.Email && { email: branch.Email }),
    contactNo: branch.ContactNo,
    addressLine0: branch.Address.Line0,
    ...(branch.Address.Line1 && { addressLine1: branch.Address.Line1 }),
    ...(branch.Address.Line2 && { addressLine2: branch.Address.Line2 }),
    postalZone: branch.Address.PostalZone,
    cityName: branch.Address.City,
    state: branch.Address.State,
  }
}

export function toApiBranchWithLicense(branch: BranchWithLicense): BranchWithLicenseDTO {
  return {
    businessId: branch.BusinessId,
    name: branch.Name?.length > 0 ? branch.Name : '-',
    ...(branch.Email && { email: branch.Email }),
    contactNo: branch.ContactNo,
    addressLine0: branch.Address.Line0,
    ...(branch.Address.Line1 && { addressLine1: branch.Address.Line1 }),
    ...(branch.Address.Line2 && { addressLine2: branch.Address.Line2 }),
    postalZone: branch.Address.PostalZone,
    cityName: branch.Address.City,
    state: branch.Address.State,
    ...(branch.ValidSince && { validSince: formatDate(new Date(branch.ValidSince), 'yyyy-MM-dd', APP_LOCALE) }),
    ...(branch.ValidUntil && { validUntil: formatDate(new Date(branch.ValidUntil), 'yyyy-MM-dd', APP_LOCALE) }),
    ...(branch.AccountNo && { accountNo: branch.AccountNo }),
    ...(branch.Remark && { remark: branch.Remark }),
  }
}

export function toBranchWithLicense(branch: BusinessBranch, license: LicenseFields): BranchWithLicense {
  return {
    ...branch,
    ...license
  }
}

// --- Validator ----------------------------------------------------------------------------------

export function isNullBusinessBranch(branch: BusinessBranch): string[] {
  let errors: string[] = [];
  if (branch.BusinessId == null)
    errors.push(ERROR_MESSAGES.EMPTY_FIELD('Business'));
  if (branch.ContactNo == null || branch.ContactNo.trim().length === 0)
    errors.push(ERROR_MESSAGES.EMPTY_FIELD('Contact No.'));
  errors = [...errors, ...isNullAddress(branch.Address)];
  return errors;
}

export function isNullBranchWithLicense(branch: BranchWithLicense): string[] {
  const errors: string[] = [...isNullBusinessBranch(branch)];
  if (branch.ValidSince == null)
    errors.push(ERROR_MESSAGES.EMPTY_FIELD('Valid From'));
  if (branch.ValidUntil == null)
    errors.push(ERROR_MESSAGES.EMPTY_FIELD('Valid Until'));
  return errors;
}